import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { DebounceSettings } from 'lodash'; // eslint-disable-line lodash/import-scope -- Doesn't apply to importing types
import type { ActionCreator, AnyAction } from 'redux';

import useDebouncedCallback from '../../../hooks/useDebouncedCallback';
import * as PackageInventoryStore from '../../../redux/packageInventory';
import TeamsStore from '../../../redux/teams';

const DEBOUNCE_TIMEOUT = 750;

//  We want requests to happen after the last user action takes place.
//  Read more: https://css-tricks.com/debouncing-throttling-explained-examples/#aa-debounce
const DEBOUNCE_OPTIONS: DebounceSettings = {
  leading: false,
  trailing: true,
};

/**
 * Hook which will return a function which dispatches a given action
 * first, followed by dispatching a debounced version of the async fetchPackages.
 */
const useFetchPackages = (actionCreator: ActionCreator<AnyAction>, resetPage: boolean = false) => {
  const dispatch = useDispatch();

  //  Memoized debounce function, which re-fetches the packages.
  const dispatchFetchPackages = useDebouncedCallback(() => {
    dispatch(PackageInventoryStore.actions.fetchPackages());
  }, DEBOUNCE_TIMEOUT, DEBOUNCE_OPTIONS);

  //  This helper does three things, in synchronous order:
  //  - dispatch an action which will update the state immediately in response to user action
  //  - dispatch preFetchLoading to tell the UI that something is loading right away.
  //  - after the debounce timeout, the packages are fetched
  const fetchPackages = useCallback((...args) => {
    if (resetPage) {
      dispatch(PackageInventoryStore.actions.resetPackagesPage());
    }

    //  Dispatch the passed in action, which updates the Redux store immediately
    dispatch(actionCreator(...args));

    //  Update the UI right away so the user knows something is happening.
    dispatch(PackageInventoryStore.actions.preFetchLoading());

    dispatchFetchPackages();
  }, [actionCreator, dispatch, dispatchFetchPackages, resetPage]);

  return fetchPackages;
};

/**
 * We also need to check when the teamIds change, but they are stored in the teams slice of the store.
 * This hook essentially "spies" on teams.teamSelection, and will re-fetch the packages when it changes.
 *
 * @param {() => void} resetSelectedPageInURL - Function which resets the selected page in the URL.
 */
export const useRefetchPackagesOnTeamSelection = (resetSelectedPageInURL: () => void) => {
  const dispatch = useDispatch();
  const teamSelection = useSelector(TeamsStore.selectors.getTeamSelection);

  useEffect(() => {
    dispatch(PackageInventoryStore.actions.fetchPackages());
    //  Reset the packages page, so that the existing selected package is cleared out.
    resetSelectedPageInURL();
  }, [dispatch, resetSelectedPageInURL, teamSelection]);
};

export default useFetchPackages;
